import { forwardRef, useMemo, useState } from "react";
import { Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Button from "../../common/components/Button";
import Card from "../../common/components/Card";
import Checkbox from "../../common/components/Checkbox";
import DataTable from "../../common/components/DataTable";
import DefaultButton from "../../common/components/DefaultButton";
import { Col, Row } from "../../common/components/Grid";
import Icon from "../../common/components/Icon";
import ReactForm from "../../common/components/ReactForm";
import TextareaInput from "../../common/components/TextareaInput";
import TextInput from "../../common/components/TextInput";
import useCredentials from "../../common/hooks/credentialsHook";
import useNetwork from "../../common/hooks/networkHook";
import { ReactNodeDialog } from "../../common/utils/dialogUtlities";
import { deepClone, deepEquals } from "../../common/utils/objectUtilities";
import { dynamicSort } from "../../common/utils/tableUtilities";
import { canInsertRequiredcomment, canRemoveRequiredcomment, canUpdateRequiredcomment, insertCommentField, insertRequiredComment, removeRequiredComment, remvoeCommentField, updateCommentField, updateRequiredComment, useAllRequiredComments } from "../../reducers/requiredCommentSlice";
import SimpleSettings from "./SimpleSettings";
import PropTypes from "prop-types";
import useClientConfig from "../../common/hooks/clientConfigHook";
import { DragTarget, DropTarget } from "../../common/components/DragDropComponents";
import { ensureArray } from "../../common/utils/arrayUtilities";

const defaultSort = ['title']
const newCommentTemplate = { applications: [], fields: [] };
const newFieldTemplate = {};

const CommentField = ({ field, idx, onChange }) => {
    const { t } = useTranslation();
    const [newValueList, setNewValueList] = useState();
    const [newValue, setNewValue] = useState();
    const data = idx !== undefined ? { sourceIndex: idx } : null;

    return <>
        <ReactNodeDialog title={t('value')} showModal={!!newValueList} onClose={() => { setNewValue(); setNewValueList(); }} onSave={() => { onChange('values', newValueList); setNewValueList(); setNewValue(); }}>
            <Table>
                <thead>
                    <tr>
                        <th>{t('value')}</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {newValueList?.length > 0 && newValueList.map((value, idx) => <tr>
                        <td>{value}</td>
                        <td><DefaultButton kind="delete" onClick={() => setNewValueList(values => values.filter((_, i) => i !== idx))}></DefaultButton></td>
                    </tr>)}
                    {(!newValueList || newValueList.length < 1) && t('freeText')}
                </tbody>
                <tfoot>
                    <tr>
                        <td>
                            <TextInput value={newValue} placeholder={t('value')} live={true} hideLabel={true} onChange={value => setNewValue(value)}></TextInput>
                        </td>
                        <td><DefaultButton kind="add" disabled={!newValue} onClick={() => { setNewValueList(values => [...values, newValue]); setNewValue(); }}></DefaultButton></td>
                    </tr>
                </tfoot>
            </Table>
        </ReactNodeDialog>
        <Row>
            <Col className="mb-2 mb-md-0" md>
                <Row>
                    { idx !== undefined && <DragTarget kind="requiredComment" version={1} data={data}>
                        <Col className="mx-2" xs="auto" style={{ cursor: 'grab', width: '28px' }}>
                            <Icon variant="solid" kind="grip-lines-vertical"></Icon>
                        </Col>
                    </DragTarget> }
                    <Col xs>
                        <TextInput value={field.label} placeholder={t('label')} hideLabel={true} onChange={value => onChange('label', value)}></TextInput>
                    </Col>
                </Row>
            </Col>
            <Col className="mb-2 mb-md-0 ml-5 ml-md-0" md>
                <TextInput value={field.placeholder} placeholder={t('placeholder')} hideLabel={true} onChange={value => onChange('placeholder', value)}></TextInput>
            </Col>
            <Col className="mb-2 mb-md-0 ml-5 ml-md-0 text-right" md="auto">
                <Checkbox value={field.required} hideLabel={true}   placeholder={t('required')} onChange={value => onChange('required', value)}></Checkbox>
            </Col>
        </Row>
        <Row>
            <Col className="mb-2 mb-md-0" md>
                <Row>
                    <Col className="mx-2" xs="auto" style={{ width: '28px' }}></Col>
                    <Col className="mx-1 mt-2" xs="auto">
                        {t('permittedValues')}
                    </Col>
                    <Col className="mx-1" xs>
                        {field.values?.length > 0 ? <div className="tags d-flex flex-row flex-wrap">
                            {field.values.map(v => <div className="tag" key={v}>{v}</div>)}
                        </div> : <div className="mt-2">{t('freeText')}</div>}
                    </Col>
                    <Col xs="auto">
                        <DefaultButton className="mt-2" kind="edit" size="sm" variant="default" hideLabel={true} onClick={() => setNewValueList(field.values ? [...field.values] : [])}></DefaultButton>
                    </Col>
                </Row>
            </Col>
        </Row>
    </>
}

const RequiredCommentLabel = ({ value: comment }) => {
    return <Row>
        <Col className="text-left">
            {comment.title}
        </Col>
    </Row>;
}

const RequiredCommentDetails = forwardRef(({ value: comment, isNew, disabled, onChange }, ref) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const network = useNetwork();
    const [newField, setNewField] = useState(deepClone(newFieldTemplate));
    const fields = useMemo(() => ensureArray(comment.fields), [comment.fields]);

    const displayFields = useMemo(() => {
        const displayFields = [...fields];
        displayFields.sort(dynamicSort(['sequence']));
        return displayFields;
    }, [fields]);

    const handleFieldUpdate = (commentField, field, value) => {
        if (isNew) {
            onChange({ fields: fields.map(f => f === commentField ? { ...f, [field]: value } : f)})
        } else {
            updateCommentField(network, { id: commentField.id, [field]: value });
        }
    }

    const moveFieldUp = (index) => {
        const sequence1 = displayFields[index].sequence;
        const sequence2 = displayFields[index - 1].sequence
        if (isNew) {
            const fields = [...displayFields];
            fields[index] = { ...fields[index], sequence: sequence2 };
            fields[index - 1] = { ...fields[index - 1], sequence: sequence1 };
            onChange({ fields });
        } else {
            handleFieldUpdate(displayFields[index], 'sequence', sequence2);
            handleFieldUpdate(displayFields[index - 1], 'sequence', sequence1);
        }
    }

    const moveFieldDown = (index) => {
        const sequence1 = displayFields[index].sequence;
        const sequence2 = displayFields[index + 1].sequence
        if (isNew) {
            const fields = [...displayFields];
            fields[index] = { ...fields[index], sequence: sequence2 };
            fields[index + 1] = { ...fields[index + 1], sequence: sequence1 };
            onChange({ fields });
        } else {
            handleFieldUpdate(displayFields[index], 'sequence', sequence2);
            handleFieldUpdate(displayFields[index + 1], 'sequence', sequence1);
        }
    }

    const moveField = (sourceIndex, destIndex) => {
        const start = Math.min(sourceIndex, destIndex);
        const end = Math.max(sourceIndex, destIndex);
        const indices = displayFields.slice(start, end + 1).map(step => step.sequence);
        if (sourceIndex < destIndex) {
            indices.unshift(indices.pop())
        } else {
            indices.push(indices.shift());
        }
        if (isNew) {
            const fields = [...displayFields];
            for (let i = start; i <= end; ++i) {
                fields[i] = { ...fields[i], sequence: indices[i - start] };
            }
            onChange({ fields });
        } else {
            for (let i = start; i <= end; ++i) {
                handleFieldUpdate(displayFields[i], 'sequence', indices[i - start]);
            }
        }
    }

    const removeField = (index) => {
        if (isNew) {
            onChange({ fields: fields.filter(f => f !== displayFields[index] )});
        } else {
            remvoeCommentField(network, displayFields[index]);
        }
    }

    const addField = () => {
        const sequence = fields.reduce((sequence, field) => !sequence || sequence < field.sequence ? field.sequence : sequence, 0) + 1;
        if (isNew) {
            onChange({ fields: [ ...fields, { ...newField, sequence }]})
        } else {
            insertCommentField(network, { ...newField, sequence, commentid: comment.id })
        }
        setNewField(deepClone(newFieldTemplate));
    }

    const columns = useMemo(() => {
        const columns = [
            { type: 'type', id: 'type', field: ['site','level','type'], label: t('type', { count: 1 }), title: t('type', { count: 1 }), sortfield: 'type', strict: false, wildcard: t('anyWildcard') },
            { type: 'problem', id: 'problem', field: ['site','level','type','problem'], label: t('problem', { count: 1 }), title: t('problem', { count: 1 }), sortfield: 'problem', strict: false },
            { type: 'option', id: 'event', field: 'event', label: t('dispatchEvent'), title: t('dispatchEvent'), sortfield: 'event', options: ['New', 'On Scene', 'End', 'Re-open'], required: true}
        ]
        if (clientConfig.enableLevels) {
            columns.unshift({ type: 'level', id: 'level', field: ['site','level','type'], label: t('level', { count: 1 }), title: t('level', { count: 1 }), sortfield: 'level', strict: false, wildcard: t('anyWildcard') })
        }
        if (clientConfig.enableSites) {
            columns.unshift({ type: 'site', id: 'site', field: ['site','level','type'], label: t('site', { count: 1 }), title: t('site', { count: 1 }), sortfield: 'site', strict: false, wildcard: t('anyWildcard') })
        }
        return columns;
    }, [t, clientConfig.enableSites, clientConfig.enableLevels]);

    return <ReactForm ref={ref}>
        <Row>
            <Col>
                <TextInput value={comment.title} maxLength={256} placeholder={t('title')} onChange={title => onChange({ title })} required></TextInput>
            </Col>
        </Row>
        <Row>
            <Col>
                <TextareaInput value={comment.message} rows={6} maxLength={1024} placeholder={t('message')} onChange={message => onChange({ message})} required></TextareaInput>
            </Col>
        </Row>
        <Card title={t('applications')}>
            <DataTable columns={columns} value={comment.applications ?? []} defaultSort={['site','level','type','problem','priority']} 
            canAdd={entry => {
                return entry.event && (!comment.applications || comment.applications?.every(a => !deepEquals(a, entry)))
            }}
            onAdd={application => onChange({ applications: [ ...comment.applications, { ...application, mode: 'Dispatch' } ] })} 
            onRemove={application => onChange({ applications: comment.applications.filter(a => !deepEquals(a, application)) })}></DataTable>
        </Card>
        <Card title={t('field', { count: fields?.length ?? 0 })}>
            <div className="d-flex flex-column">
                {displayFields.map((field, idx) => <DropTarget kind="requiredComment" version={1} onDrop={data => moveField(data.sourceIndex, idx)}>
                    <Row key={field.id} className="mb-2 pb-2" style={{ borderBottom: '1px solid lightgrey' }}>
                        <Col md>
                            <DragTarget kind="requiredComment" version={1} data={{ sourceIndex: idx }}>
                                <CommentField field={field} idx={idx} onChange={(f, v) => handleFieldUpdate(field, f, v)}></CommentField>
                            </DragTarget>
                        </Col>
                        <Col className="text-right" md="auto">
                            <Row>
                                <Col></Col>
                                { idx > 0 ? <Col className="mx-1" xs="auto" style={{ width: '36px' }}>
                                    <Button variant="outline-dark" onClick={() => moveFieldUp(idx)}><Icon intent="up"></Icon></Button>
                                </Col> : <Col className="mx-1 d-none d-md-block" xs="auto" style={{ width: '36px' }}></Col> }
                                { idx < fields.length - 1 ? <Col className="mx-1" xs="auto" style={{ width: '36px' }}>
                                    <Button variant="outline-dark" onClick={() => moveFieldDown(idx)}><Icon intent="down"></Icon></Button>
                                </Col> : <Col className="mx-1 d-none d-md-block" xs="auto" style={{ width: '36px' }}></Col> }
                                <Col className="mx-1" xs="auto">
                                    <DefaultButton kind="delete" variant="outline-danger" style={{ width: '80px' }} onClick={() => removeField(idx)}></DefaultButton>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </DropTarget>)}
                <Row className="mb-2">
                    <Col md>
                        <CommentField field={newField} onChange={(field, value) => setNewField(newField => ({ ...newField, [field]: value }))}></CommentField>
                    </Col>
                    <Col className="text-right" md="auto">
                        <Row>
                            <Col></Col>
                            <Col className="mx-1" xs="auto" style={{ width: '36px' }}></Col>
                            <Col className="mx-1" xs="auto" style={{ width: '36px' }}></Col>
                            <Col className="mx-1" xs="auto">
                                <DefaultButton kind="add" variant="outline-primary" disabled={!newField.label || !newField.placeholder} style={{ width: '80px' }} onClick={() => addField()}></DefaultButton>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </div>
        </Card>
    </ReactForm>
});

const RequiredComments = ({ uuid, label, onClose }) => {
    const { t } = useTranslation();
    const network = useNetwork();
    const credentials = useCredentials();
    const comments = useAllRequiredComments();
    const sortFields = useMemo(() => {
        let fields = { name: t('name') }
        return fields;
    }, [t]);

    const handleAdd = comment => {
        return insertRequiredComment(network, comment);
    }

    return <SimpleSettings data={comments} uuid={uuid} label={label} pkey="id" defaultSort={defaultSort} sortFields={sortFields}
        templateItem={newCommentTemplate} canAdd={comment => canInsertRequiredcomment(credentials, comment)}
        canUpdate={comment => canUpdateRequiredcomment(credentials, comment)}
        canRemove={comment => canRemoveRequiredcomment(credentials, comment)}
        onAdd={handleAdd} onRemove={comment => removeRequiredComment(network, comment)}
        onChange={(comment, delta) => updateRequiredComment(network, { id: comment.id, ...delta })}
        onClose={onClose} ItemLabel={RequiredCommentLabel} ItemDetails={RequiredCommentDetails}
    ></SimpleSettings>
}
RequiredComments.propTypes = {
    /**
     * ID to identify the settings status entry
     */
    uuid: PropTypes.string.isRequired,
    /**
     * Label for the screen
     */
    label: PropTypes.string.isRequired,
    /**
     * Function to call when the screen is closed
     */
    onClose: PropTypes.func
}

export default RequiredComments;